import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button} from 'reactstrap';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import {formatMissingFinancialLabel, navigateForNotification} from 'components/Header/Notifications';

export const MISSING_FINANCIAL_TYPE = 11;

const parseNotificationPayload = (notification) => {
	const data = notification?.Data ?? notification?.data;
	if (data == null || data === '' || data === '{}') {
		return null;
	}
	if (typeof data === 'object') {
		return data;
	}
	try {
		return JSON.parse(data);
	} catch (e) {
		return null;
	}
};

const getCreatedTs = (notification) => {
	const created = notification?.Created ?? notification?.created;
	const ts = created ? Date.parse(created) : 0;
	return Number.isFinite(ts) ? ts : 0;
};

const getRowKey = (notification, index) => {
	const payload = parseNotificationPayload(notification);
	const pkey = payload?.pkey ?? payload?.data?.workflow_pkey ?? notification?.Pkey ?? notification?.pkey;
	return pkey != null ? String(pkey) : String(index);
};

/**
 * Persistent Helpdesk banner for today's missing labor/financials (type 11).
 * Stays until the workflow is marked dealt with - clicking does not dismiss it.
 */
const MissingFinancialBanner = ({notifications = [], history}) => {
	const [expanded, setExpanded] = useState(false);

	const queue = useMemo(() => {
		const seen = new Set();
		return (notifications || [])
			.filter((item) => Number(item.Type ?? item.type) === MISSING_FINANCIAL_TYPE)
			.slice()
			.sort((a, b) => getCreatedTs(a) - getCreatedTs(b))
			.filter((item) => {
				const key = getRowKey(item, seen.size);
				if (seen.has(key)) {
					return false;
				}
				seen.add(key);
				return true;
			});
	}, [notifications]);

	useEffect(() => {
		if (queue.length <= 1) {
			setExpanded(false);
		}
	}, [queue.length]);

	const current = queue[0] ?? null;
	const remaining = Math.max(queue.length - 1, 0);

	const handleNavigate = useCallback((event, notification) => {
		event?.stopPropagation();
		const target = notification ?? current;
		if (!target) {
			return;
		}
		const payload = parseNotificationPayload(target) || {url: 'helpdesks/missing_financial'};
		if (!payload.url) {
			payload.url = 'helpdesks/missing_financial';
		}
		navigateForNotification(payload, history);
	}, [current, history]);

	const handleToggleExpanded = useCallback((event) => {
		event?.stopPropagation();
		event?.preventDefault();
		setExpanded((prev) => !prev);
	}, []);

	const renderRow = (notification, {showExpand} = {}) => (
		<div
			className="missing-financial-banner-row"
			role="button"
			tabIndex={0}
			onClick={(event) => handleNavigate(event, notification)}
			onKeyDown={(event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					handleNavigate(event, notification);
				}
			}}
		>
			<div className="missing-financial-banner-copy">
				<strong>{formatMissingFinancialLabel(notification)}</strong>
				{showExpand && remaining > 0 && (
					<button
						type="button"
						className="missing-financial-banner-more"
						onClick={handleToggleExpanded}
						aria-expanded={expanded}
						aria-label={expanded ? 'Collapse missing financial list' : `Show ${remaining} more missing financials`}
					>
						<span>(+{remaining} more)</span>
						{expanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
					</button>
				)}
			</div>
			<div className="missing-financial-banner-actions">
				<Button size="sm" color="light" onClick={(event) => handleNavigate(event, notification)}>
					Open queue
				</Button>
			</div>
		</div>
	);

	if (!current) {
		return null;
	}

	return (
		<div className={`missing-financial-banner${expanded ? ' is-expanded' : ''}`}>
			{renderRow(current, {showExpand: true})}
			{expanded && remaining > 0 && (
				<div className="missing-financial-banner-list">
					{queue.slice(1).map((notification, index) => (
						<React.Fragment key={getRowKey(notification, index + 1)}>
							{renderRow(notification)}
						</React.Fragment>
					))}
				</div>
			)}
		</div>
	);
};

export default MissingFinancialBanner;
